import axios from '@fs/zion-axios'
import zionDebug from '@fs/zion-debug'
import { i18n } from '@fs/zion-locale'
import { sgBaseUrl } from '@fs/zion-config'
import { getUser } from '@fs/zion-user'

import { DELETABLE } from '../../helpers'

const debugError = zionDebug('zh:services:error')

// For error handling of non signed-in user
class AccountNotAuthorizedError extends Error {
  constructor(message = '', ...args) {
    super(message, ...args)
    this.name = 'AccountNotAuthorizedError'
  }
}

// Retrieve all available data for a pedigree for a person.
// User must be signed in.
export async function getPedigree({
  id,
  spouseId,
  generationsUp = 5,
  generationsDown = 0,
  includeAllSpousesAndChildren = false,
  includeSpouseAncestry = false,
  includeSuggestions = false,
  includePedigreeLooping = false,
} = {}) {
  // Make sure we have a valid user before we try to get the pedigree
  let user
  try {
    // Redirects to the login page if the user is not logged in
    // AND we are on an authenticated route
    user = await getUser()
  } catch (error) {
    // Throws an error if we are not logged in
    // AND we are not on an authenticated route
    return Promise.reject(error)
  }

  if (id === 'undefined') {
    id = user.personId
  }

  const url = `
    ${sgBaseUrl}/service/tree/tf/pedigree/hourglass/
    ${id || user.personId}
    ?structure=graph
    &gensup=${generationsUp}
    &gendown=${generationsDown}
    &includeAllSpousesDescendancy=${includeAllSpousesAndChildren}
    &includeSpouseAncestry=${includeSpouseAncestry}
    &includeSuggestions=${includeSuggestions}
    &includePedigreeLooping=${includePedigreeLooping}
    ${spouseId ? `&spouseId=${spouseId}` : ''}
  `

  return axios
    .get(url.replace(/\s/g, ''), {
      headers: {
        'Accept-Language': i18n.language,
      },
    })
    .catch((error) => {
      if (error?.response?.data?.error === 'User cannot log in') {
        throw new AccountNotAuthorizedError('You are not authorized to use FamilySearch Family Tree.', { cause: error })
      }

      debugError('Could not retrieve pedigree.', error)
    })
}

// Retrieve a portrait for a person, if any.
// If no portrait is available an empty portrait object is returned.
export async function getPortrait(pid = 'CURRENT') {
  return axios
    .get(`${sgBaseUrl}/service/memories/tps/persons/${pid}/portrait`, {
      headers: {
        Accept: 'application/vnd.familysearch.mem.tps-v2+json',
      },
    })
    .catch((error) => {
      debugError('Could not retrieve portrait.', error)
    })
}

function chunkArray(arr, chunkSize) {
  const arrayOfChunks = []
  for (let i = 0, len = arr.length; i < len; i += chunkSize) arrayOfChunks.push(arr.slice(i, i + chunkSize))
  return arrayOfChunks
}

// Retrieve portraits for a list of persons, if any.
export async function getPortraits(pids = []) {
  if (!pids.length) return []
  // Portraits API only supports up to 20 pids at a time
  const chunkedPids = chunkArray(pids, 20)
  const portraitCalls = chunkedPids.map((chunkOfPids) =>
    axios.get(`${sgBaseUrl}/service/memories/tps/persons/portraits?pid=${chunkOfPids.join(',')}`, {
      headers: { Accept: 'application/json' },
    })
  )

  return Promise.all(portraitCalls).then((portraitResults) => {
    let resultsObj = {}
    portraitResults.forEach((res) => {
      if (res?.data?.results) {
        resultsObj = { ...resultsObj, ...res.data.results }
      }
    })
    return resultsObj
  })
}

// Check if person is editable in this experience
// We only want users to be able to edit those they created and no one else has interacted with.
// Criteria is must be deletable and no groupId
export async function isPersonEditable(person) {
  if (person?.groupId || !person?.id) return false

  return axios
    .get(`${sgBaseUrl}/service/tree/tf/person/${person.id}/delete-constraint`)
    .then(({ data = {} }) => [DELETABLE.TRUE, DELETABLE.HOME_PERSON].includes(data[DELETABLE.PARAM]))
    .catch((error) => {
      debugError('Could not retrieve person or delete constraint.', error)
      return false
    })
}

export async function getPersonObj(pid) {
  if (!pid) return false
  return axios.get(`/service/tree/tree-data/v8/person/${pid}/card`)
}

export async function setPofGender(pid, conclusion) {
  if (!pid || !conclusion) return false
  return axios.put(`${sgBaseUrl}/service/tree/tree-data/person/${pid}/conclusion/${conclusion.id}`, conclusion)
}

export default {
  getPedigree,
  getPersonObj,
  getPortrait,
  getPortraits,
  isPersonEditable,
}
